import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Avatar,
  Card,
  CardContent,
  Button,
  Tooltip,
  Stack,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Launch as LaunchIcon,
  Person as PersonIcon,
  Article as ArticleIcon,
  Topic as TopicIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Message as MessageIcon,
  Chat as ChatIcon,
  Gavel as DecisionIcon,
  VerifiedUser as VerifiedIcon,
} from '@mui/icons-material';
import { GraphNode, GraphEdge, NodeType, EdgeType, GraphScope } from '../../../types/graph.types';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import GraphAnnotationPanel from '../GraphAnnotations/GraphAnnotationPanel';

interface GraphDetailsPanelProps {
  node: GraphNode;
  edges?: GraphEdge[];
  nodes?: GraphNode[];
  scope?: GraphScope;
  annotationCount?: number;
  onRelatedNodeClick?: (node: GraphNode) => void;
  onClose?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`details-tabpanel-${index}`}
      aria-labelledby={`details-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ paddingTop: 16, paddingBottom: 16 }}>{children}</div>}
    </div>
  );
}

/**
 * GraphDetailsPanel Component
 *
 * Displays detailed information about a selected node or edge in the graph.
 * Shows properties, relationships, and provides quick actions.
 *
 * Features:
 * - Node properties display
 * - Related nodes exploration
 * - Quick actions (edit, delete, share)
 * - Expandable sections
 * - Multi-tab interface for complex nodes
 */
const GraphDetailsPanel: React.FC<GraphDetailsPanelProps> = ({
  node,
  edges = [],
  nodes = [],
  scope = 'personal',
  annotationCount = 0,
  onRelatedNodeClick,
  onClose,
}) => {
  const navigate = useNavigate();
  const featureFlags = useFeatureFlags();
  const [tabValue, setTabValue] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['properties', 'relationships'])
  );

  // Semantic edge type display config
  const semanticEdgeConfig: Record<string, { label: string; color: string }> = {
    [EdgeType.SUPPORTS]: { label: 'Supports', color: '#4DC4D2' },
    [EdgeType.CONTRADICTS]: { label: 'Contradicts', color: '#E94257' },
    [EdgeType.CAUSES]: { label: 'Causes', color: '#325BA8' },
    [EdgeType.DEPENDS_ON]: { label: 'Depends On', color: '#892987' },
  };

  const semanticEdgeTypes = React.useMemo(() => new Set([
    EdgeType.SUPPORTS,
    EdgeType.CONTRADICTS,
    EdgeType.CAUSES,
    EdgeType.DEPENDS_ON,
  ]), []);

  // Get node icon based on type
  const getNodeIcon = (type: NodeType) => {
    switch (type) {
      case NodeType.MESSAGE:
        return <MessageIcon />;
      case NodeType.CONVERSATION:
        return <ChatIcon />;
      case NodeType.DOCUMENT:
        return <ArticleIcon />;
      case NodeType.CONCEPT:
        return <CategoryIcon />;
      case NodeType.PERSON:
        return <PersonIcon />;
      case NodeType.TOPIC:
        return <TopicIcon />;
      case NodeType.ORGANIZATION:
        return <BusinessIcon />;
      default:
        return <CategoryIcon />;
    }
  };

  // Get node color based on type
  const getNodeColor = (type: NodeType) => {
    const colors = {
      [NodeType.MESSAGE]: '#4056A6',
      [NodeType.CONVERSATION]: '#892987',
      [NodeType.DOCUMENT]: '#325BA8',
      [NodeType.CONCEPT]: '#4DC4D2',
      [NodeType.PERSON]: '#892987',
      [NodeType.TOPIC]: '#E94257',
      [NodeType.ORGANIZATION]: '#999999',
    };
    return colors[type] || '#666666';
  };

  // Toggle section expansion
  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString();
  };

  // Extract node-specific properties
  const getNodeProperties = () => {
    const baseProps = {
      'ID': node.id,
      'Type': node.type,
      'Label': node.label,
      'Namespace': node.namespace,
      'Created': formatDate(node.createdAt),
      'Updated': formatDate(node.updatedAt),
    };

    // Add type-specific properties
    if ('properties' in node && node.properties) {
      return { ...baseProps, ...node.properties };
    }

    return baseProps;
  };

  // Compute related nodes from edges
  const relatedNodes = React.useMemo(() => {
    const relationships: Array<{
      node: GraphNode | undefined;
      edge: GraphEdge;
      direction: 'incoming' | 'outgoing';
    }> = [];

    // Find all edges connected to this node
    edges.forEach(edge => {
      if (edge.source === node.id) {
        // Outgoing edge
        const targetNode = nodes.find(n => n.id === edge.target);
        relationships.push({
          node: targetNode,
          edge,
          direction: 'outgoing'
        });
      } else if (edge.target === node.id) {
        // Incoming edge
        const sourceNode = nodes.find(n => n.id === edge.source);
        relationships.push({
          node: sourceNode,
          edge,
          direction: 'incoming'
        });
      }
    });

    return relationships;
  }, [node.id, edges, nodes]);

  // Filter semantic relationships from edges
  const semanticRelationships = React.useMemo(() => {
    return relatedNodes.filter(
      (rel) => semanticEdgeTypes.has(rel.edge.type as EdgeType)
    );
  }, [relatedNodes, semanticEdgeTypes]);

  // Check if this node can be analyzed in Decision Mode (Topic or Concept)
  const canAnalyzeInDecisionMode =
    featureFlags.decisionMode &&
    (node.type === NodeType.TOPIC || node.type === NodeType.CONCEPT);

  // Extract conversation UUID from node properties
  // Dgraph stores predicates as "Conversation.id", "Message.conversationId" etc.
  const getConversationId = (): string | undefined => {
    const props = (node as any).properties;
    if (!props) return undefined;

    if (node.type === NodeType.CONVERSATION) {
      return props['Conversation.id'] ||
             props['Conversation.nodeId'] ||
             props.conversation_id ||
             props.conversationId;
    }

    if (node.type === NodeType.MESSAGE) {
      return props['Message.conversationId'] ||
             props.conversationId ||
             props.conversation_id ||
             node.metadata?.conversationId;
    }

    return undefined;
  };

  // Extract message UUID from node properties
  const getMessageId = (): string | undefined => {
    const props = (node as any).properties;
    if (!props) return undefined;
    return props['Message.messageId'] || props['Message.id'] || props.messageId;
  };

  // Handle opening node in chat (for MESSAGE and CONVERSATION nodes)
  const handleOpenInChat = () => {
    const conversationId = getConversationId();

    if (!conversationId) {
      console.warn('📍 GRAPH → CHAT: No conversation ID found for node', node);
      return;
    }

    if (node.type === NodeType.CONVERSATION) {
      navigate(`/chat?conversationId=${conversationId}`);
      console.log('📍 GRAPH → CHAT: Navigating to conversation', {
        conversationId,
        nodeLabel: node.label
      });
      return;
    }

    // For MESSAGE nodes, include message ID for scroll-to
    const messageId = getMessageId();
    const url = messageId
      ? `/chat?conversationId=${conversationId}&messageId=${messageId}`
      : `/chat?conversationId=${conversationId}`;
    navigate(url);
    console.log('📍 GRAPH → CHAT: Navigating to conversation', {
      conversationId,
      messageId,
      nodeLabel: node.label
    });
  };

  // Check if this node can be opened in chat - only when a real conversation UUID exists
  const canOpenInChat =
    (node.type === NodeType.CONVERSATION || node.type === NodeType.MESSAGE) &&
    !!getConversationId();

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: 16, borderBottom: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Avatar style={{ backgroundColor: getNodeColor(node.type) }}>
            {getNodeIcon(node.type)}
          </Avatar>
          <div style={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap>
              {node.label}
            </Typography>
            <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
              {node.type.charAt(0).toUpperCase() + node.type.slice(1)} Node
            </Typography>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Properties" />
          <Tab label="Relationships" />
          <Tab label="Activity" />
          <Tab
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                Annotations
                {annotationCount > 0 && (
                  <Chip
                    label={annotationCount}
                    size="small"
                    color="error"
                    style={{ height: 18, fontSize: '0.65rem', minWidth: 18 }}
                  />
                )}
              </div>
            }
          />
        </Tabs>
      </div>

      {/* Content - single scrollable area */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {/* Properties Tab */}
        <TabPanel value={tabValue} index={0}>
          <div style={{ padding: 16 }}>
            {/* Properties Section */}
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  marginBottom: 8,
                }}
                onClick={() => toggleSection('properties')}
              >
                <Typography variant="subtitle2" style={{ flexGrow: 1 }}>
                  Node Properties
                </Typography>
                <IconButton size="small">
                  {expandedSections.has('properties') ?
                    <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </div>

              {expandedSections.has('properties') && (
                <Card variant="outlined">
                  <CardContent style={{ padding: 16 }}>
                    {Object.entries(getNodeProperties()).map(([key, value]) => {
                      // Special handling for very long content
                      // Check if value is an array or object for special formatting
                      const isArray = Array.isArray(value);
                      const isObject = typeof value === 'object' && !isArray && value !== null;

                      return (
                        <div
                          key={key}
                          style={{ marginBottom: 16 }}
                        >
                          {/* Label - Always on its own line for clarity */}
                          <Typography
                            variant="body2"
                            style={{
                              color: 'rgba(0,0,0,0.6)',
                              fontWeight: 600,
                              marginBottom: 4,
                              textTransform: 'capitalize',
                            }}
                          >
                            {key.replace(/_/g, ' ')}:
                          </Typography>

                          {/* Value Container */}
                          <div style={{
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            borderRadius: 4,
                            border: '1px solid rgba(0,0,0,0.12)',
                            padding: 12,
                          }}>
                            {isArray ? (
                              // Format arrays as chips or list
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {value.map((item: any, index: number) => (
                                  <Chip
                                    key={index}
                                    label={String(item)}
                                    size="small"
                                    variant="outlined"
                                    style={{ maxWidth: '200px' }}
                                  />
                                ))}
                              </div>
                            ) : isObject ? (
                              // Format objects as indented JSON
                              <Typography
                                variant="body2"
                                component="pre"
                                style={{
                                  margin: 0,
                                  fontSize: '0.875rem',
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {JSON.stringify(value, null, 2)}
                              </Typography>
                            ) : (
                              // Regular text value
                              <Typography
                                variant="body2"
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontSize: '0.875rem',
                                  lineHeight: 1.6,
                                }}
                              >
                                {String(value)}
                              </Typography>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Metadata Section */}
            {node.metadata && (
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: 8,
                  }}
                  onClick={() => toggleSection('metadata')}
                >
                  <Typography variant="subtitle2" style={{ flexGrow: 1 }}>
                    Metadata
                  </Typography>
                  <IconButton size="small">
                    {expandedSections.has('metadata') ?
                      <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </div>

                {expandedSections.has('metadata') && (
                  <Card variant="outlined">
                    <CardContent style={{ padding: 8 }}>
                      <div style={{
                        backgroundColor: '#fafafa',
                        borderRadius: 4,
                        padding: 8,
                      }}>
                        <pre style={{
                          margin: 0,
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {JSON.stringify(node.metadata, null, 2)}
                        </pre>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </TabPanel>

        {/* Relationships Tab */}
        <TabPanel value={tabValue} index={1}>
          <div style={{ padding: 16 }}>
            <Typography variant="subtitle2" style={{ marginBottom: 16 }}>
              Connected Nodes ({relatedNodes.length})
            </Typography>
            {relatedNodes.length === 0 ? (
              <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', padding: 16, textAlign: 'center' }}>
                No connections found
              </Typography>
            ) : (
            <List>
              {relatedNodes.map((relationship, index) => {
                const connectedNode = relationship.node;
                const edge = relationship.edge;
                const isIncoming = relationship.direction === 'incoming';

                if (!connectedNode) {
                  // Node not found, show edge info only
                  return (
                    <ListItem key={`rel-${index}`} style={{ opacity: 0.6 }}>
                      <ListItemIcon>
                        <Avatar style={{ width: 32, height: 32, backgroundColor: '#9e9e9e' }}>
                          ?
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={isIncoming ? edge.source : edge.target}
                        secondary={
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Chip
                              label={edge.type || edge.label}
                              size="small"
                              color={isIncoming ? 'primary' : 'secondary'}
                              style={{ fontSize: '0.75rem' }}
                            />
                            <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                              {isIncoming ? '→ this' : 'this →'}
                            </Typography>
                          </span>
                        }
                      />
                    </ListItem>
                  );
                }

                return (
                <ListItem
                  key={`rel-${index}`}
                  button
                  onClick={() => {
                    if (onRelatedNodeClick && connectedNode) {
                      onRelatedNodeClick(connectedNode);
                    }
                  }}
                >
                  <ListItemIcon>
                    <Avatar
                      style={{
                        backgroundColor: connectedNode ? getNodeColor(connectedNode.type) : '#9e9e9e',
                        width: 32,
                        height: 32,
                      }}
                    >
                      {connectedNode ? getNodeIcon(connectedNode.type) : '?'}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={connectedNode?.label || connectedNode?.id || 'Unknown'}
                    secondary={
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Chip
                            label={edge.type || edge.label}
                            size="small"
                            color={isIncoming ? 'primary' : 'secondary'}
                            style={{ fontSize: '0.75rem' }}
                          />
                          <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                            {isIncoming ? 'incoming' : 'outgoing'}
                          </Typography>
                        </span>
                        {connectedNode?.type && (
                          <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                            Type: {connectedNode.type}
                          </Typography>
                        )}
                      </span>
                    }
                  />
                  <Tooltip title="Open node details">
                    <IconButton size="small" edge="end">
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
                );
              })}
            </List>
            )}

            {/* Semantic Relationships Section */}
            {featureFlags.semanticRelationships && semanticRelationships.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <Typography variant="subtitle2" style={{ marginBottom: 16 }}>
                  Semantic Relationships ({semanticRelationships.length})
                </Typography>
                <Stack spacing={1}>
                  {semanticRelationships.map((rel, index) => {
                    const config = semanticEdgeConfig[rel.edge.type] || { label: rel.edge.type, color: '#999' };
                    const confidence = rel.edge.metadata?.confidence as number | undefined;
                    const verified = rel.edge.metadata?.verified as boolean | undefined;
                    const context = rel.edge.metadata?.context as string | undefined;
                    const targetLabel = rel.node?.label || (rel.direction === 'outgoing' ? rel.edge.target : rel.edge.source);

                    return (
                      <Paper
                        key={`sem-${index}`}
                        variant="outlined"
                        style={{
                          padding: 12,
                          borderLeft: `3px solid ${config.color}`,
                          cursor: rel.node ? 'pointer' : 'default',
                        }}
                        onClick={() => {
                          if (rel.node && onRelatedNodeClick) {
                            onRelatedNodeClick(rel.node);
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <Chip
                            label={config.label}
                            size="small"
                            style={{
                              backgroundColor: config.color,
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                          <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                            {rel.direction === 'outgoing' ? 'this →' : '→ this'}
                          </Typography>
                          {verified && (
                            <Tooltip title="Verified relationship">
                              <VerifiedIcon style={{ fontSize: 14, color: '#4DC4D2' }} />
                            </Tooltip>
                          )}
                          {confidence != null && (
                            <Chip
                              label={`${Math.round(confidence * 100)}%`}
                              size="small"
                              variant="outlined"
                              style={{
                                fontSize: '0.65rem',
                                height: 20,
                                marginLeft: 'auto',
                                color: confidence >= 0.7 ? '#4DC4D2' : confidence >= 0.4 ? '#999' : '#E94257',
                                borderColor: confidence >= 0.7 ? '#4DC4D2' : confidence >= 0.4 ? '#999' : '#E94257',
                              }}
                            />
                          )}
                        </div>
                        <Typography variant="body2" noWrap>
                          {targetLabel}
                        </Typography>
                        {context && (
                          <Typography
                            variant="caption"
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              marginTop: 4,
                              color: 'rgba(0,0,0,0.6)',
                            } as React.CSSProperties}
                          >
                            {context}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              </div>
            )}
          </div>
        </TabPanel>

        {/* Activity Tab */}
        <TabPanel value={tabValue} index={2}>
          <div style={{ padding: 16 }}>
            <Typography variant="subtitle2" style={{ marginBottom: 16 }}>
              Recent Activity
            </Typography>
            <Stack spacing={1}>
              <Paper variant="outlined" style={{ padding: 8 }}>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                  2 hours ago
                </Typography>
                <Typography variant="body2">
                  Node was referenced in a new document
                </Typography>
              </Paper>
              <Paper variant="outlined" style={{ padding: 8 }}>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                  Yesterday
                </Typography>
                <Typography variant="body2">
                  Properties were updated
                </Typography>
              </Paper>
              <Paper variant="outlined" style={{ padding: 8 }}>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                  3 days ago
                </Typography>
                <Typography variant="body2">
                  Node was created
                </Typography>
              </Paper>
            </Stack>
          </div>
        </TabPanel>

        {/* Annotations Tab */}
        <TabPanel value={tabValue} index={3}>
          <GraphAnnotationPanel
            nodeId={node.id}
            scope={scope}
          />
        </TabPanel>
      </div>

      {/* Actions */}
      <div style={{ padding: 16, borderTop: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {canOpenInChat && (
            <Button
              size="small"
              startIcon={<ChatIcon />}
              variant="contained"
              color="primary"
              onClick={handleOpenInChat}
              style={{ marginBottom: 8 }}
            >
              Open in Chat
            </Button>
          )}
          {canAnalyzeInDecisionMode && (
            <Button
              size="small"
              startIcon={<DecisionIcon />}
              variant="contained"
              onClick={() => navigate(`/decision?thesis=${encodeURIComponent(node.label)}`)}
              style={{
                marginBottom: 8,
                backgroundColor: '#4DC4D2',
              }}
            >
              Analyze in Decision Mode
            </Button>
          )}
          <Button
            size="small"
            startIcon={<EditIcon />}
            variant="outlined"
          >
            Edit
          </Button>
          <Button
            size="small"
            startIcon={<ShareIcon />}
            variant="outlined"
          >
            Share
          </Button>
          <Button
            size="small"
            startIcon={<DeleteIcon />}
            variant="outlined"
            color="error"
          >
            Delete
          </Button>
        </Stack>
      </div>
    </div>
  );
};

export default GraphDetailsPanel;
